/**
 * Image validation utility for StudyExpo platform
 * Provides size requirements and validation for different image types
 */

export const IMAGE_REQUIREMENTS = {
  logo: {
    name: 'Institute Logo',
    recommended: {
      width: 250,
      height: 100,
      aspectRatio: '2.5:1'
    },
    alternative: [
      { width: 200, height: 200, label: 'Square Logo' },
      { width: 150, height: 60, label: 'Small Logo' }
    ],
    maxSize: 100 * 1024, // 100KB
    maxSizeMB: 0.1,
    formats: ['PNG (transparent background recommended)', 'JPG', 'WebP'],
    acceptedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
  },
  banner: {
    name: 'Virtual Booth Banner',
    recommended: {
      width: 1920,
      height: 600,
      aspectRatio: '16:5'
    },
    alternative: [
      { width: 1024, height: 500, label: 'Tablet Banner' },
      { width: 768, height: 400, label: 'Mobile Banner' }
    ],
    maxSize: 500 * 1024, // 500KB
    maxSizeMB: 0.5,
    formats: ['JPG', 'PNG', 'WebP'],
    acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  },
  campusTour: {
    name: '360° Campus Tour Image',
    recommended: {
      width: 4096,
      height: 2048,
      aspectRatio: '2:1 (Equirectangular)'
    },
    maxSize: 2 * 1024 * 1024, // 2MB
    maxSizeMB: 2,
    formats: ['JPG', 'PNG'],
    acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png']
  },
  general: {
    name: 'Image',
    maxSize: 5 * 1024 * 1024, // 5MB
    maxSizeMB: 5,
    formats: ['JPG', 'PNG', 'WebP'],
    acceptedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  }
};

/**
 * Validate image file against requirements
 * @param {File} file - The image file to validate
 * @param {string} imageType - Type of image ('logo', 'banner', 'campusTour', 'general')
 * @returns {Object} { valid: boolean, error: string | null }
 */
export const validateImageFile = (file, imageType = 'general') => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  const requirements = IMAGE_REQUIREMENTS?.[imageType] || IMAGE_REQUIREMENTS?.general;

  // Check file type
  if (!requirements?.acceptedTypes?.includes(file?.type)) {
    return {
      valid: false,
      error: `Invalid file type. Please upload ${requirements?.formats?.join(', ')}`
    };
  }

  // Check file size
  if (file?.size > requirements?.maxSize) {
    return {
      valid: false,
      error: `File size exceeds ${requirements?.maxSizeMB}MB limit. Please compress your image.`
    };
  }

  return { valid: true, error: null };
};

/**
 * Get formatted size requirements message for display
 * @param {string} imageType - Type of image ('logo', 'banner', 'campusTour', 'general')
 * @returns {string} Formatted requirements message
 */
export const getImageRequirementsMessage = (imageType = 'general') => {
  const req = IMAGE_REQUIREMENTS?.[imageType];
  
  if (!req) return '';

  let message = `📏 **${req?.name} Requirements:**\n\n`;
  
  if (req?.recommended) {
    message += `✅ **Recommended Size:** ${req?.recommended?.width} × ${req?.recommended?.height}px (${req?.recommended?.aspectRatio})\n`;
  }
  
  if (req?.alternative && req?.alternative?.length > 0) {
    message += `\n**Alternative Sizes:**\n`;
    req?.alternative?.forEach(alt => {
      message += `   • ${alt?.label}: ${alt?.width} × ${alt?.height}px\n`;
    });
  }
  
  message += `\n📦 **Max File Size:** ${req?.maxSizeMB}MB`;
  message += `\n📄 **Formats:** ${req?.formats?.join(', ')}`;
  
  return message;
};

/**
 * Get short inline notice for image upload inputs
 * @param {string} imageType - Type of image
 * @returns {string} Short notice text
 */
export const getImageUploadNotice = (imageType = 'general') => {
  const req = IMAGE_REQUIREMENTS?.[imageType];
  
  if (!req) return '';
  
  if (req?.recommended) {
    return `Recommended: ${req?.recommended?.width}×${req?.recommended?.height}px, Max: ${req?.maxSizeMB}MB`;
  }
  
  return `Max file size: ${req?.maxSizeMB}MB`;
};